import {
  Button,
  ExplicitSelectionButton,
  Unifree,
  useParentPostMessage,
  YesObject
} from '@yes.technology/react-toolkit'
import jwtDecode from 'jwt-decode'
import { useTranslation } from 'react-i18n-lite'

import { useAuthentication } from 'authentication/shared/hooks'
import { TokenData, User } from 'shared/types'
import { getFormattedTimezone } from 'shared/utils/timezone'
import { ProjectFields } from 'yesObject/shared/hooks/type'
import useYesObject from 'yesObject/shared/hooks/useYesObject'
import ActiveSessionLoading from './ActiveSessionLoading'
import HierarchyCardWithAreaLine from './HierarchyCardWithAreaLine'
import {
  ActiveSessionContainer,
  ActiveSessionTitle,
  UserDataHierarchyCard
} from './styles'

const emptyCredentials: TokenData['credentials'] = {
  user_uuid: '',
  organization_uuid: '',
  applicationsystem_uuid: ''
}

const PROJECT_FIELDS: ProjectFields = {
  sid: 1,
  des: 1
}

const ActiveSession = () => {
  const { t, language } = useTranslation()

  const { authenticatedData, waTimezone } = useAuthentication()

  const token = authenticatedData?.token || ''

  const { credentials, user_id: userIdentityUuid } = token
    ? jwtDecode<TokenData>(token)
    : { credentials: emptyCredentials, user_id: '' }

  const {
    user_uuid: userCredentialsUuid,
    organization_uuid: organizationCredentialsUuid,
    applicationsystem_uuid: applicationSystemCredentialsUuid
  } = credentials

  const { yesObject: user, isLoading: isLoadingUser } = useYesObject<User>({
    objectUUID: userIdentityUuid || '',
    objectclassUUID: window.uuidObjectclassUser,
    fetchOnLoad: true,
    projectFields: PROJECT_FIELDS,
    showDisplayError: false
  })

  const { yesObject: userCredentials, isLoading: isLoadingUserCredentials } =
    useYesObject<User>({
      objectUUID: userCredentialsUuid,
      objectclassUUID: window.uuidObjectclassUser,
      fetchOnLoad: true,
      projectFields: PROJECT_FIELDS,
      showDisplayError: false
    })

  const {
    yesObject: organizationCredentials,
    isLoading: isLoadingOrganization
  } = useYesObject<YesObject>({
    objectUUID: organizationCredentialsUuid,
    objectclassUUID: window.uuidObjectclassOrganization,
    fetchOnLoad: true,
    showDisplayError: false
  })

  const {
    yesObject: applicationSystemCredentials,
    isLoading: isLoadingApplicationSystem
  } = useYesObject<YesObject>({
    objectUUID: applicationSystemCredentialsUuid,
    objectclassUUID: window.uuidObjectclassApplicationSystem,
    fetchOnLoad: true,
    showDisplayError: false
  })

  const { postMessage } = useParentPostMessage()

  const logout = () => {
    postMessage({ logout: true })
  }

  const modifyCredentials = () => {
    postMessage({ modifyCredentials: true })
  }

  const authenticationMethods = [
    {
      uuid: 'password',
      des: t('authentication-methods.password')
    }
  ]

  const isFetchingCredentials =
    isLoadingUser ||
    isLoadingOrganization ||
    isLoadingApplicationSystem ||
    isLoadingUserCredentials

  if (isFetchingCredentials) {
    return <ActiveSessionLoading />
  }

  return (
    <ActiveSessionContainer>
      <ActiveSessionTitle>{t('active-session.title')}</ActiveSessionTitle>

      <Button heightClass='regular' onClick={logout}>
        {t('active-session.logout')}
      </Button>

      <UserDataHierarchyCard spacingClass='extraSmall' level={1}>
        <HierarchyCardWithAreaLine
          title={t('active-session.id-and-credentials')}
        >
          <Unifree
            informational
            required
            label={t('active-session.user-identity')}
            value={`${user?.sid || ''} | ${user?.des || ''}`}
            enableValueFading
            enableLabelFading
          />

          <Unifree
            informational
            required
            label={t('login.identification.language')}
            value={t('language-name')}
            enableValueFading
            enableLabelFading
          />

          {waTimezone && (
            <Unifree
              informational
              required
              label={t('active-session.timezone')}
              value={
                waTimezone ? getFormattedTimezone(waTimezone, language) : ''
              }
              enableValueFading
              enableLabelFading
            />
          )}
        </HierarchyCardWithAreaLine>

        <HierarchyCardWithAreaLine
          title={t('active-session.user-authentication')}
        >
          <ExplicitSelectionButton
            disabled
            selectionType='multiselection'
            heightClass='extraSmall'
            direction='vertical'
            optionLabelsPosition='right'
            options={authenticationMethods}
            checkedOption={{ password: true }}
            onChange={() => undefined}
          />
        </HierarchyCardWithAreaLine>

        <HierarchyCardWithAreaLine title={t('credentials.title')}>
          <Unifree
            informational
            required
            label={t('active-session.ecosystem-credentials')}
            value='Yes Network'
            className='mb-2'
            enableValueFading
            enableLabelFading
          />
          <Unifree
            informational
            required
            label={t('active-session.applicationsystem-credential')}
            value={`${applicationSystemCredentials?.sid || ''} | ${applicationSystemCredentials?.des || ''}`}
            className='mb-2'
            enableValueFading
            enableLabelFading
          />
          <Unifree
            informational
            required
            label={t('active-session.organization-credential')}
            value={`${organizationCredentials?.sid || ''} | ${organizationCredentials?.des || ''}`}
            enableValueFading
            enableLabelFading
          />
          <Unifree
            informational
            required
            label={t('active-session.user-credential')}
            value={`${userCredentials?.sid || ''} | ${userCredentials?.des || ''}`}
            enableValueFading
            enableLabelFading
          />

          <Button variant='secondary' onClick={modifyCredentials}>
            {t('active-session.modify-credentials')}
          </Button>
        </HierarchyCardWithAreaLine>
      </UserDataHierarchyCard>
    </ActiveSessionContainer>
  )
}

export default ActiveSession
